// LanguageOnboardingModal — shown once per user on first Game load until
// they pick a language. Big flag grid (5 langs). Choice is persisted both
// to localStorage (`gw_language_chosen` sentinel + `gw_language`) and to
// the character document via PATCH /api/character/preferences.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { Globe, Check } from "lucide-react";

// Localized welcome copy — the modal renders in every language at once so
// the player recognizes their tongue before making a choice. Keeps the
// UX approachable for players who don't know English yet.
const WELCOME = {
  it:  { title: "In che lingua vuoi giocare?", hint: "Puoi cambiarla in qualsiasi momento dalle Impostazioni.", confirm: "Conferma" },
  en:  { title: "Choose your language", hint: "You can change it anytime from Settings.", confirm: "Confirm" },
  de:  { title: "Wähle deine Sprache", hint: "Du kannst sie jederzeit in den Einstellungen ändern.", confirm: "Bestätigen" },
  fil: { title: "Anong wika ang gusto mong gamitin?", hint: "Maaari mong palitan ito anumang oras sa Mga Setting.", confirm: "Kumpirmahin" },
  ru:  { title: "Выберите язык", hint: "Вы можете изменить его в любой момент в Настройках.", confirm: "Подтвердить" },
};

export default function LanguageOnboardingModal({ onDismiss }) {
  const { i18n } = useTranslation();
  const [picked, setPicked] = useState(i18n.language);
  const [saving, setSaving] = useState(false);
  const active = WELCOME[picked] || WELCOME.en;

  const preview = (code) => {
    setPicked(code);
    i18n.changeLanguage(code);
    try { localStorage.setItem("gw_language", code); } catch (e) { /* noop */ }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await api.patchPreferences({ language: picked });
    } catch (e) { /* silent — client-side lang still works */ }
    try { localStorage.setItem("gw_language_chosen", "1"); } catch (e) { /* noop */ }
    onDismiss?.(picked);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(5,5,5,0.9)", backdropFilter: "blur(4px)" }}
      data-testid="language-onboarding-modal">
      <div className="panel p-5 w-full max-w-lg space-y-4">
        <div className="flex items-center gap-2 border-b border-[#262626] pb-2">
          <Globe className="w-4 h-4 text-[#E5C34A]" strokeWidth={1.5} />
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">
            {active.title}
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2">
          {SUPPORTED_LANGUAGES.map(lang => (
            <button key={lang.code} onClick={() => preview(lang.code)}
              data-testid={`lang-onboard-${lang.code}`}
              className={`flex flex-col items-center gap-1.5 py-3 border transition-all
                ${picked === lang.code
                  ? "border-[#E55A00] bg-[#E55A00]/10 scale-105"
                  : "border-[#262626] hover:border-[#E5C34A] opacity-70 hover:opacity-100"}`}>
              <span className="text-3xl leading-none">{lang.flag}</span>
              <span className="font-mono text-[9px] uppercase tracking-widest text-[#E2DFD8]">{lang.label}</span>
              {picked === lang.code && <Check className="w-3 h-3 text-[#E55A00]" strokeWidth={2.5} />}
            </button>
          ))}
        </div>

        <div className="font-mono text-[10px] text-[#8c8c8c] text-center italic">
          {active.hint}
        </div>

        <div className="flex justify-end pt-2 border-t border-[#262626]">
          <button data-testid="lang-onboard-confirm" onClick={confirm} disabled={saving}
            className="px-4 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 disabled:opacity-40 flex items-center gap-1.5">
            <Check className="w-3 h-3" /> {active.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
